/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

const DOMElementHelper = globalThis.gbc.classes.DOMElementHelper;

(function () {
  globalThis.browserInfo = {
    isFirefox: false,
    isEdge: false,
    isChrome: false,
    isOpera: false,
    isSafari: false
  };

  const sUsrAg = globalThis.navigator.userAgent;

  if (sUsrAg.includes("Edge")) {
    globalThis.browserInfo.isEdge = true;
  } else if (sUsrAg.includes("Chrome")) {
    globalThis.browserInfo.isChrome = true;
  } else if (sUsrAg.includes("Safari")) {
    globalThis.browserInfo.isSafari = true;
  } else if (sUsrAg.includes("Opera")) {
    globalThis.browserInfo.isOpera = true;
  } else if (sUsrAg.includes("Firefox")) {
    globalThis.browserInfo.isFirefox = true;
  }
})();

// Compute ScrollBar size
(function () {
  const div = document.createElement("div");
  div.style.width = "50px";
  div.style.height = "50px";
  div.style.overflowY = "scroll";
  div.style.position = "absolute";
  div.style.top = "-200px";
  div.style.left = "-200px";
  div.innerHTML = '<div style="height:100px;width:100%"></div>';

  document.body.appendChild(div);
  const w1 = div.offsetWidth;
  const w2 = div.children[0].offsetWidth;
  div.remove();

  globalThis.scrollBarSize = w1 - w2;
})();

/**
 * Helper to use Window
 * @namespace classes.WindowHelper
 */
class WindowHelper /** @lends classes.WindowHelper */ {
  static _opened = [];
  /**
   * open browser window
   * @param url
   * @param once
   * @return {Window}
   */
  static openWindow = function (url, once) {
    if (once) {
      if (WindowHelper._opened.includes(url)) {
        return null;
      }
      WindowHelper._opened.push(url);
    }
    return window.open(url);
  };
  /**
   * close browser window
   */
  static closeWindow = function () {
    window.close();
  };

  /**
   * Return true if mobile browser, false otherwise
   * @returns bool if the browser is mobile
   */
  static isMobile = function () {
    const testExp = /BlackBerry|Windows Phone|Opera Mini|IEMobile|Mobile|webOS/i;
    const isOtherMobile = testExp.test(navigator.userAgent) === true;
    const isAndroid = WindowHelper.isAndroid();
    const isIOS = WindowHelper.isIOS();
    // window.orientation works well but is deprecated. So we use additional check.
    return window.orientation !== undefined || isAndroid || isIOS || isOtherMobile;
  };

  static isPhone = function () {
    if (WindowHelper.isMobile()) {
      return Math.min(window.screen.width, window.screen.height) < 768;
    }
    return false;
  };

  static isTablet = function () {
    if (WindowHelper.isMobile()) {
      return Math.min(window.screen.width, window.screen.height) >= 768;
    }
    return false;
  };

  static isAndroid = function () {
    if (WindowHelper.isAndroidEmulationDebug()) {
      return true;
    }
    const testExp = /Android/i;
    // window.orientation works well but is deprecated. So we use additional check.
    return window.orientation !== undefined && testExp.test(navigator.userAgent) === true;
  };

  static isIOS = function () {
    const testExp = /iPhone|iPad|iPod|GMI/i;
    if (testExp.test(globalThis.navigator.userAgent)) {
      return true;
    }
    return globalThis.navigator.maxTouchPoints > 2 && /MacIntel/.test(globalThis.navigator.platform);
  };

  static isTouchDevice = function () {
    return "ontouchstart" in document.documentElement || "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
  };

  static isAndroidEmulationDebug = function () {
    return WindowHelper.isURLParameterEnabled(window.location.search, "androidemulationdebug");
  };

  /**
   * Check if url has a parameter with corresponding value
   * @param {string} url - url to check parameter on
   * @param {string} param - parameter
   * @param {string} value - value of parameter
   * @returns {boolean} true if parameter with corresponding value has been found in url
   */
  static hasParameterValue = function (url, param, value) {
    const queryString = (param + "=" + value).toLowerCase();
    // extract url parameters only
    const start = url.lastIndexOf("?") + 1;
    let end = url.lastIndexOf("#"); // manage optional hash section located at the end of the url
    if (end < start) {
      end = url.length;
    }
    const variables = url.substring(start, end).toLowerCase().split("&"); // get array of parameters
    for (const element of variables) {
      if (element === queryString) {
        return true;
      }
    }
    return false;
  };

  /**
   * Check if url has the parameter set as active (1 value)
   * @param {string?} url - optional. If unset, we look by default for current browser url
   * @param {string} param - parameter name to check
   * @returns {boolean} true if parameter is set and enabled in url
   */
  static isURLParameterEnabled = function (url, param) {
    if (param === undefined) {
      param = url;
      url = window.location.search;
    }
    return WindowHelper.hasParameterValue(url, param, "1");
  };

  /**
   * Check if url has the parameter set as inactive (0 value)
   * @param {string?} url - optional. If unset, we look by default for current browser url
   * @param {string} param - parameter name to check
   * @returns {boolean} true if parameter is set and disabled in url
   */
  static isURLParameterDisabled = function (url, param) {
    if (param === undefined) {
      param = url;
      url = window.location.search;
    }
    return WindowHelper.hasParameterValue(url, param, "0");
  };

  /**
   * Convert image to base64
   * @param {String} src
   * @param {Function} callback
   * @param {String} outputFormat - image/png, image/jpeg
   */
  static toDataURL = function (src, callback, outputFormat) {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = function () {
      let canvas = document.createElement("CANVAS");
      const ctx = canvas.getContext("2d");
      // Resize the canvas to the original image dimensions
      canvas.height = window.naturalHeight;
      canvas.width = window.naturalWidth;
      ctx.drawImage(this, 0, 0);
      let dataURL = canvas.toDataURL(outputFormat);
      callback(dataURL);
      canvas = null;
    };
    img.src = src;
    // Make sure the load event fires for cached images too
    if (img.complete || img.complete === undefined) {
      // Flush cache
      img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
      // Try again
      img.src = src;
    }
  };

  /**
   * Get the current selected text
   * @returns {?string}
   */
  static getSelectionText = function () {
    const activeElement = document.activeElement;
    let selectionText = null;
    if (activeElement) {
      // try to get selection form activeElement
      selectionText = DOMElementHelper.getSelectionText(activeElement);
    }
    if (selectionText === null && window.getSelection) {
      // if it is impossible to retrieve selection from activeElement
      // try window.getSelection()
      selectionText = window.getSelection().toString();
    }
    return selectionText;
  };
}

modulum("WindowHelper", ["DOMElementHelper"], function (context, cls) {
  cls.WindowHelper = WindowHelper;
});

if (typeof module !== "undefined" && module.exports) {
  module.exports = WindowHelper; // same as    export default ServerHelper;
  module.exports.default = WindowHelper; // same as    export default ServerHelper;
}
